/**
 * LeadIntelligenceSettingsPanel — grouped admin UI for ADR-009 Lead Intelligence flags.
 */

import React, { useMemo } from "react";
import { Form, Input, InputNumber, Space, Switch, Tag, Typography } from "antd";
import styled from "styled-components";
import ExpandableSections from "@/shared/components/ExpandableSections.jsx";
import {
  LEAD_INTELLIGENCE_SECTIONS,
  LEAD_INTELLIGENCE_SETTING_KEYS,
  getLeadIntelligenceFieldMeta,
} from "../utils/leadIntelligenceSettingsMeta.js";

const { Text, Paragraph } = Typography;

const FieldBlock = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionHint = styled(Paragraph)`
  && {
    margin-top: 0;
    margin-bottom: 12px;
  }
`;

const PanelIntro = styled(Paragraph)`
  && {
    margin-bottom: 16px;
  }
`;

/**
 * @param {object} props
 * @param {object[]} [props.settings] - Loaded lead-intelligence setting rows
 */
const LeadIntelligenceSettingsPanel = ({ settings = [] }) => {
  const settingsByKey = useMemo(() => {
    const map = {};
    settings.forEach((setting) => {
      map[setting.key] = setting;
    });
    return map;
  }, [settings]);

  const fieldsBySection = useMemo(() => {
    /** @type {Record<string, string[]>} */
    const grouped = {};
    for (const section of LEAD_INTELLIGENCE_SECTIONS) {
      grouped[section.key] = [];
    }

    const keys = new Set([
      ...LEAD_INTELLIGENCE_SETTING_KEYS,
      ...settings.map((s) => s.key),
    ]);

    keys.forEach((key) => {
      const meta = getLeadIntelligenceFieldMeta(key);
      if (!meta) {
        return;
      }
      const sectionKey = meta.section || "rollout";
      if (!grouped[sectionKey]) {
        grouped[sectionKey] = [];
      }
      grouped[sectionKey].push(key);
    });

    return grouped;
  }, [settings]);

  const sections = LEAD_INTELLIGENCE_SECTIONS.map((section) => {
    const keys = fieldsBySection[section.key] || [];
    if (keys.length === 0) {
      return null;
    }

    return {
      key: section.key,
      title: section.title,
      children: (
        <>
          {section.hint && (
            <SectionHint type="secondary">{section.hint}</SectionHint>
          )}
          {keys.map((key) => {
            const meta = getLeadIntelligenceFieldMeta(key);
            const row = settingsByKey[key];
            const type = meta?.type || row?.type || "string";
            const description = meta?.description || row?.description;
            const metadata = meta?.metadata || row?.metadata || {};

            let control = <Input />;
            if (type === "boolean") {
              control = <Switch />;
            } else if (type === "number") {
              control = (
                <InputNumber
                  style={{ width: "100%" }}
                  min={metadata.min}
                  max={metadata.max}
                  step={metadata.step || 1}
                />
              );
            }

            return (
              <FieldBlock key={key}>
                <Form.Item
                  name={key}
                  valuePropName={type === "boolean" ? "checked" : "value"}
                  label={
                    <Space size={8}>
                      <span>{meta?.label || key}</span>
                      <Tag color="gold">admin</Tag>
                    </Space>
                  }
                  style={{ marginBottom: 0 }}
                >
                  {control}
                </Form.Item>
                {description && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {description}
                  </Text>
                )}
                {metadata.unit && (
                  <Tag color="blue" style={{ marginTop: 4 }}>
                    Unit: {metadata.unit}
                  </Tag>
                )}
              </FieldBlock>
            );
          })}
        </>
      ),
    };
  }).filter(Boolean);

  return (
    <div data-testid="lead-intelligence-settings-panel">
      <PanelIntro type="secondary">
        Lead Intelligence (ADR-009) — private System Settings. Enable master +
        widgets to show overview on Leads and Lead Pulse on the dashboard.
      </PanelIntro>
      <ExpandableSections
        sections={sections}
        defaultOpenKeys={["rollout"]}
        accordion={false}
      />
    </div>
  );
};

export default LeadIntelligenceSettingsPanel;
